/**
 * CustomerSchedule — month-grid calendar view of upcoming Lutrom
 * visits at the customer's sites.
 *
 * Data source: `/api/schedule` — already customer-scoped on the
 * server (returns their own shifts + WO-derived virtual shifts
 * only).
 *
 * We deliberately don't reuse the admin `ScheduleCalendar` widget
 * here — that component leans on admin-only drag-to-edit, financial
 * tooltips and per-engineer filters that a customer neither needs
 * nor should see. This is a compact, read-only month grid answering
 * one question: "when are Lutrom next at my sites?"
 *
 * Behaviour:
 *   • Previous / next month navigation (via ChevronLeft / Right).
 *   • Each day cell shows up to 3 event pills; a "+N more" pill
 *     covers overflow.
 *   • Click a day to open a bottom-sheet listing every visit for
 *     that day, with links into the WO detail for virtual shifts.
 *   • Multi-day WO windows expand across their span so a Wed-Fri
 *     booking appears on all three days.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Calendar, User, Clock, MapPin, ClipboardList, X, Trash2 } from 'lucide-react';
import { PageHeader } from '../../components/Shared';
import { getSchedule } from '../../services/api';

const WEEKDAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const isoDay = (d) => d.toISOString().slice(0, 10);
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const monthLabel = (d) => d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
const fmtDay = (dateStr) => {
  try {
    return new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-GB', {
      weekday: 'long', day: 'numeric', month: 'long',
    });
  } catch { return dateStr; }
};

/**
 * Build the 6×7 grid of Date objects that make up a month view.
 * Starts on Monday and always renders 42 cells so calendar rows
 * don't jump between months.
 */
const buildMonthGrid = (anchor) => {
  const first = startOfMonth(anchor);
  const jsDow = first.getDay();                    // 0 = Sunday
  const mondayOffset = jsDow === 0 ? 6 : jsDow - 1;
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - mondayOffset);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    return d;
  });
};

const CustomerSchedule = () => {
  const [anchor, setAnchor] = useState(() => new Date());
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);   // ISO day string for the bottom sheet

  const monthStart = useMemo(() => startOfMonth(anchor), [anchor]);
  const monthEnd = useMemo(() => endOfMonth(anchor), [anchor]);

  // Pad the fetch window by one week either side so the calendar's
  // leading/trailing (grey) days are populated too.
  const range = useMemo(() => {
    const from = new Date(monthStart); from.setDate(from.getDate() - 7);
    const to = new Date(monthEnd);     to.setDate(to.getDate() + 7);
    return { from: isoDay(from), to: isoDay(to) };
  }, [monthStart, monthEnd]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getSchedule({ from: range.from, to: range.to, includeWorkOrders: true })
      .then(rows => { if (!cancelled) { setShifts(rows || []); setError(null); } })
      .catch(e => { if (!cancelled) setError(e?.response?.data?.detail || 'Could not load schedule'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range.from, range.to]);

  // Bucket every shift into every day it covers. Multi-day WO
  // windows fan out; single-day admin-created shifts sit on their
  // one day. All entries land in the same day-keyed map so the grid
  // render is O(1) per cell.
  const eventsByDay = useMemo(() => {
    const map = new Map();
    const add = (dateStr, s) => {
      if (!dateStr) return;
      if (!map.has(dateStr)) map.set(dateStr, []);
      map.get(dateStr).push(s);
    };
    (shifts || []).forEach(s => {
      if (s.source === 'work_order' && s.startByDate && s.finishByDate) {
        const start = new Date(`${s.startByDate}T00:00:00`);
        const end = new Date(`${s.finishByDate}T00:00:00`);
        if (isNaN(+start) || isNaN(+end) || end < start) {
          add(s.date || s.startByDate, s);
          return;
        }
        const cursor = new Date(start);
        while (cursor <= end) {
          add(isoDay(cursor), s);
          cursor.setDate(cursor.getDate() + 1);
        }
      } else {
        add(s.date, s);
      }
    });
    return map;
  }, [shifts]);

  const grid = useMemo(() => buildMonthGrid(anchor), [anchor]);
  const todayStr = isoDay(new Date());
  const selectedEvents = selected ? (eventsByDay.get(selected) || []) : [];

  const jumpMonth = (delta) => {
    const next = new Date(anchor);
    next.setDate(1);
    next.setMonth(next.getMonth() + delta);
    setAnchor(next);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="customer-schedule-page">
      <PageHeader
        title="Our schedule at your sites"
        description="When Lutrom Engineering are next planned to attend. Click any day for details."
      />

      {/* Month navigation */}
      <div className="flex items-center gap-3">
        <button
          className="p-2 rounded-md border border-slate-200 bg-white hover:bg-slate-50"
          onClick={() => jumpMonth(-1)}
          data-testid="schedule-prev-month"
          title="Previous month"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="font-display uppercase tracking-wide text-slate-900 min-w-[10rem] text-center" data-testid="schedule-month-label">
          {monthLabel(anchor)}
        </div>
        <button
          className="p-2 rounded-md border border-slate-200 bg-white hover:bg-slate-50"
          onClick={() => jumpMonth(1)}
          data-testid="schedule-next-month"
          title="Next month"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
        <button
          className="ml-auto text-xs px-3 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50"
          onClick={() => setAnchor(new Date())}
          data-testid="schedule-today"
        >
          Today
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-800 text-sm rounded-md">{error}</div>
      )}

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {/* Weekday header */}
        <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600 font-semibold">
          {WEEKDAY_HEADERS.map(d => (
            <div key={d} className="px-2 py-2 text-center">{d}</div>
          ))}
        </div>
        {/* Day grid */}
        <div className="grid grid-cols-7">
          {grid.map((d) => {
            const dayStr = isoDay(d);
            const inMonth = d.getMonth() === anchor.getMonth();
            const isToday = dayStr === todayStr;
            const events = eventsByDay.get(dayStr) || [];
            const shown = events.slice(0, 3);
            const overflow = events.length - shown.length;
            return (
              <button
                key={dayStr}
                onClick={() => setSelected(dayStr)}
                data-testid={`schedule-day-${dayStr}`}
                className={`min-h-[92px] border-b border-r border-slate-100 p-1.5 text-left transition-colors ${
                  inMonth ? 'bg-white hover:bg-slate-50' : 'bg-slate-50/60 text-slate-400 hover:bg-slate-100/60'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-semibold ${
                    isToday ? 'inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-700 text-white'
                    : inMonth ? 'text-slate-700' : 'text-slate-400'
                  }`}>
                    {d.getDate()}
                  </span>
                  {events.length > 0 && (
                    <span className="text-[9px] uppercase tracking-wider text-blue-700 font-semibold">
                      {events.length}
                    </span>
                  )}
                </div>
                <div className="space-y-0.5">
                  {shown.map((s, i) => {
                    const asset = s.assetName || (s.assetNames && s.assetNames[0]) || '';
                    const extraAssets = s.assetNames && s.assetNames.length > 1
                      ? ` +${s.assetNames.length - 1}` : '';
                    const line = asset
                      ? `${s.workOrderTitle || s.title || 'Visit'} · ${asset}${extraAssets}`
                      : (s.workOrderTitle || s.title || 'Visit');
                    return (
                      <div
                        key={`${s.id || s.workOrderId || i}-${dayStr}`}
                        className={`truncate text-[10px] rounded px-1 py-0.5 ${
                          s.source === 'work_order'
                            ? 'bg-blue-50 text-blue-800 border border-blue-100'
                            : 'bg-emerald-50 text-emerald-800 border border-emerald-100'
                        }`}
                        title={line}
                      >
                        {line}
                      </div>
                    );
                  })}
                  {overflow > 0 && (
                    <div className="text-[10px] text-slate-500 px-1">+{overflow} more</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {loading && <div className="text-slate-500 text-sm">Loading schedule…</div>}

      {/* Bottom sheet — visits on the clicked day */}
      {selected && (
        <DayDetail
          date={selected}
          events={selectedEvents}
          onClose={() => setSelected(null)}
          onPurged={(woId) => {
            // Drop every event tied to this WO across the fetched
            // month window so the calendar re-renders without the
            // ghost event immediately (no page reload needed).
            setShifts((prev) => prev.filter((s) => s.workOrderId !== woId));
          }}
        />
      )}
    </div>
  );
};

const DayDetail = ({ date, events, onClose, onPurged }) => (
  <div
    className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
    data-testid="schedule-day-detail"
    onClick={onClose}
  >
    <div
      className="w-full max-w-lg bg-white rounded-t-lg sm:rounded-lg shadow-xl max-h-[80vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between p-4 border-b border-slate-200">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">Schedule</div>
          <div className="font-display text-slate-900">{fmtDay(date)}</div>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-md hover:bg-slate-100"
          data-testid="schedule-day-close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {events.length === 0 ? (
        <div className="p-10 text-center text-slate-500 text-sm">
          <Calendar className="h-8 w-8 mx-auto mb-2 text-slate-300" />
          Nothing scheduled at your sites this day.
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {events.map((s, i) => (
            <DayEventRow key={`${s.id || s.workOrderId || i}`} shift={s} onPurged={onPurged} />
          ))}
        </div>
      )}
    </div>
  </div>
);

// Admin-only nuke escape hatch on the customer schedule popover. When
// an admin is "View portal as customer"-ing (there's a stashed admin
// token in localStorage), we surface a "Purge WO" pill so ghost/
// duplicate WOs that the customer keeps seeing can be hard-deleted
// on the spot — without having to swap out of the impersonation
// session, hunt the WO down in the admin list (which historically
// truncated to 5k), and swap back in. Uses the STASHED admin token
// so authorization succeeds even though the current session is a
// customer JWT.
const purgeWithAdminStash = async (workOrderId) => {
  const stash = localStorage.getItem('lutrom_admin_token_stash');
  if (!stash) throw new Error('No admin stash — not impersonating');
  const base = process.env.REACT_APP_BACKEND_URL;
  const res = await fetch(
    `${base}/api/work-orders/${workOrderId}/purge?force_detach=true`,
    { method: 'DELETE', headers: { Authorization: `Bearer ${stash}` } },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.detail || 'Purge failed');
  }
  return res.json();
};

const DayEventRow = ({ shift, onPurged }) => {
  const isWO = shift.source === 'work_order';
  const asset = shift.assetName || (shift.assetNames && shift.assetNames[0]) || '';
  const extraAssets = shift.assetNames && shift.assetNames.length > 1
    ? ` +${shift.assetNames.length - 1} more` : '';
  const [purging, setPurging] = useState(false);
  const isAdminImpersonating = Boolean(localStorage.getItem('lutrom_admin_token_stash'));
  const canPurge = isWO && shift.workOrderId && isAdminImpersonating;
  const handlePurge = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Permanently delete WO "${shift.workOrderTitle}" (${shift.workOrderId})?\n\nThis cannot be undone.`)) return;
    setPurging(true);
    try {
      await purgeWithAdminStash(shift.workOrderId);
      toast.success(`${shift.workOrderId} purged. Refresh to update the schedule.`);
      onPurged && onPurged(shift.workOrderId);
    } catch (err) {
      toast.error(err.message || 'Purge failed');
    } finally {
      setPurging(false);
    }
  };
  const body = (
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium text-slate-900 truncate">
        {isWO ? (shift.workOrderTitle || 'Scheduled work') : (shift.title || 'Site visit')}
      </div>
      {asset && (
        <div className="text-xs text-slate-700 mt-0.5 truncate">
          <span className="text-slate-500">Asset:</span> {asset}{extraAssets}
        </div>
      )}
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
        {!isWO && shift.shiftStart && shift.shiftEnd && (
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {shift.shiftStart}–{shift.shiftEnd}</span>
        )}
        {isWO && shift.startByDate && shift.finishByDate && (
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {shift.startByDate} → {shift.finishByDate}</span>
        )}
        {shift.site && (
          <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {shift.site}</span>
        )}
        {shift.userName && (
          <span className="inline-flex items-center gap-1"><User className="h-3 w-3" /> {shift.userName}</span>
        )}
      </div>
    </div>
  );
  const purgeButton = canPurge && (
    <button
      type="button"
      onClick={handlePurge}
      disabled={purging}
      title="Admin only — permanently delete this WO"
      className="text-xs inline-flex items-center gap-1 text-red-700 hover:bg-red-50 border border-red-200 rounded px-2 py-1 disabled:opacity-50"
      data-testid={`schedule-purge-wo-${shift.workOrderId}`}
    >
      <Trash2 className="h-3 w-3" /> {purging ? '…' : 'Purge'}
    </button>
  );
  if (isWO && shift.workOrderId) {
    return (
      <div className="flex items-start gap-3 p-4 hover:bg-slate-50 transition-colors">
        <Link
          to={`/customer/work-orders/${shift.workOrderId}`}
          className="flex-1 flex items-start gap-4 min-w-0"
          data-testid={`schedule-detail-wo-${shift.workOrderId}`}
        >
          {body}
          <ClipboardList className="h-4 w-4 text-blue-700 mt-1 shrink-0" />
        </Link>
        {purgeButton}
      </div>
    );
  }
  return (
    <div className="flex items-start gap-4 p-4">{body}</div>
  );
};

export default CustomerSchedule;
